import { scaleTime, scaleLinear, scaleBand } from "@visx/scale"
import { getExtent, getMax } from "./dataUtils"

/**
 * Creates a time scale (x-axis) from a dataset.
 *
 * @param data       - Array of data points
 * @param accessor   - Extracts a Unix timestamp (ms) from each point
 * @param rangeStart - Pixel start of the scale range (usually margin.left)
 * @param rangeEnd   - Pixel end of the scale range (usually innerWidth + margin.left)
 */
export function makeTimeScale<T>({
    data,
    accessor,
    rangeStart,
    rangeEnd,
}: {
    data: T[]
    accessor: (d: T) => number
    rangeStart: number
    rangeEnd: number
}) {
    const [min, max] = getExtent(data, accessor)
    return scaleTime({ range: [rangeStart, rangeEnd], domain: [min, max] })
}

/**
 * Creates a linear scale (y-axis) from 0 to the dataset max.
 *
 * @param data       - Array of data points
 * @param accessor   - Extracts the numeric value from each point
 * @param rangeStart - Pixel start of the scale range (usually innerHeight + margin.top — bottom of chart)
 * @param rangeEnd   - Pixel end of the scale range (usually margin.top — top of chart)
 * @param minValue   - Minimum domain ceiling (prevents a flat scale on near-zero data)
 */
export function makeLinearScale<T>({
    data,
    accessor,
    rangeStart,
    rangeEnd,
    minValue = 0,
}: {
    data: T[]
    accessor: (d: T) => number
    rangeStart: number
    rangeEnd: number
    minValue?: number
}) {
    const maxValue = Math.max(getMax(data, accessor), minValue)
    return scaleLinear({ range: [rangeStart, rangeEnd], domain: [0, maxValue], nice: true })
}

/**
 * Creates a band scale (x-axis) for categorical bar charts.
 *
 * @param domain     - Ordered list of category labels
 * @param rangeStart - Pixel start of the scale range (usually 0)
 * @param rangeEnd   - Pixel end of the scale range (usually innerWidth)
 * @param padding    - Inner padding between bands (0–1, default 0.2)
 */
export function makeBandScale({
    domain,
    rangeStart,
    rangeEnd,
    padding = 0.2,
}: {
    domain: string[]
    rangeStart: number
    rangeEnd: number
    padding?: number
}) {
    return scaleBand<string>({ range: [rangeStart, rangeEnd], domain, padding })
}
